export const SUITS = ["clubs", "diamonds", "hearts", "spades"] as const;
export const RANKS = ["9", "10", "J", "Q", "K", "A"] as const;
export const SEATS = [0, 1, 2, 3] as const;

export type Suit = (typeof SUITS)[number];
export type Rank = (typeof RANKS)[number];
export type Seat = (typeof SEATS)[number];
export type Team = 0 | 1;
export type Mode = "quick" | "daily" | "coach";
export type Phase =
  | "calling1"
  | "calling2"
  | "discard"
  | "playing"
  | "trickResult"
  | "handResult"
  | "completed";

export interface Card {
  id: string;
  suit: Suit;
  rank: Rank;
}

export interface PlayedCard {
  seat: Seat;
  card: Card;
}

export interface TrickRecord {
  cards: PlayedCard[];
  winner: Seat;
  leadSuit: Suit;
}

export interface HandSummary {
  maker: Seat;
  makerTeam: Team;
  trump: Suit;
  alone: boolean;
  tricks: [number, number];
  points: [number, number];
  reason: "made" | "march" | "euchred" | "lone-made" | "lone-march";
}

export interface DailyResult {
  date: string;
  /** Stable scenario id from the published library; absent on legacy entries. */
  scenarioId?: string;
  /**
   * Calendar the date key belongs to. "london" entries drive the London v2
   * today/streak views; absent means a legacy device-local-v1 entry that
   * stays readable history but never feeds the London calendar.
   */
  calendar?: "london";
  won: boolean;
  margin: number;
  hands: number;
  assisted: boolean;
  /** Whether the human's first calling decision matched the scenario recommendation. */
  callMatched?: boolean;
}

/** The calling decision a daily scenario is built around. */
export type DailyRecommendation = "order" | "orderAlone" | "pass";

export interface DailyScenarioFacts {
  upcardSuit: Suit;
  upcardRank: Rank;
  /** Effective trump cards in the deciding hand if the upcard suit were trump. */
  trumpCount: number;
  hasRight: boolean;
  hasLeft: boolean;
  offAces: number;
  /** Expected team-0 points per candidate, from a fixed-budget simulation. */
  orderEV: number;
  aloneEV: number;
  passEV: number;
}

export interface DailyScenario {
  /** Stable puzzle id, e.g. "tt-daily-2026-08-01". */
  id: string;
  /** YYYY-MM-DD publication date; the date -> scenario map is exact on this key. */
  publishDate: string;
  /** Deal seed; the deal is reproduced by dealHand with handNumber 0, dealer 3. */
  seed: string;
  /** What makes the scenario interesting: the order, the pass-then-call, or the alone judgment. */
  kind: "order" | "pass" | "alone";
  recommended: DailyRecommendation;
  facts: DailyScenarioFacts;
  /** Playouts per candidate action used for the recommendation. */
  simulations: number;
}

export interface LocalStats {
  games: number;
  wins: number;
  hands: number;
  gameHands: number;
  calls: number;
  callsMade: number;
  euchres: number;
  aloneCalls: number;
  aloneMade: number;
  /** Legacy device-local-v1 completion dates; kept readable, no longer written or streak-feeding. */
  dailyDates: string[];
  dailyResults: DailyResult[];
  streak: number;
}

export type Difficulty = "relaxed" | "standard" | "sharp";

export interface GameSettings {
  coach: boolean;
  sound: boolean;
  reducedMotion: boolean;
  confirmPlay: boolean;
  difficulty: Difficulty;
  locale: "en" | "zh" | "ja";
}

export interface GameState {
  version: 2;
  gameId: string;
  mode: Mode;
  seed: string;
  dailyDate?: string;
  /** London-calendar Daily identity marker; absent on legacy device-local saves. */
  dailyCalendar?: "london";
  /** Scenario id the Daily session was started from; checked on restore. */
  dailyScenarioId?: string;
  assisted: boolean;
  status: "playing" | "completed";
  phase: Phase;
  dealer: Seat;
  currentSeat: Seat;
  hands: [Card[], Card[], Card[], Card[]];
  kitty: Card[];
  upcard: Card;
  callingRound: 1 | 2;
  passed: Seat[];
  maker: Seat | null;
  trump: Suit | null;
  alone: boolean;
  inactiveSeat: Seat | null;
  currentTrick: PlayedCard[];
  tricks: TrickRecord[];
  trickCounts: [number, number];
  scores: [number, number];
  handNumber: number;
  history: string[];
  lastHand: HandSummary | null;
  winner: Team | null;
  actionNonce: number;
}

export interface PublicView {
  version: 1;
  seat: Seat;
  team: Team;
  ownHand: Card[];
  handCounts: [number, number, number, number];
  dealer: Seat;
  currentSeat: Seat;
  phase: Phase;
  callingRound: 1 | 2;
  upcard: Card;
  trump: Suit | null;
  maker: Seat | null;
  alone: boolean;
  inactiveSeat: Seat | null;
  currentTrick: PlayedCard[];
  completedTricks: TrickRecord[];
  trickCounts: [number, number];
  scores: [number, number];
  legalCardIds: string[];
  actionNonce: number;
}

export type GameAction =
  | { type: "PASS"; seat: Seat }
  | { type: "ORDER_UP"; seat: Seat; alone?: boolean }
  | { type: "CALL_SUIT"; seat: Seat; suit: Suit; alone?: boolean }
  | { type: "DISCARD"; seat: Seat; cardId: string }
  | { type: "PLAY_CARD"; seat: Seat; cardId: string }
  | { type: "COLLECT_TRICK" }
  | { type: "NEXT_HAND" };
